import { useChat } from '@ai-sdk/react'
import {
  AiChatContext,
  AnswerLanguage,
  ConversationHistory,
  EXCHANGE_DATA_PART,
  GLOSSARY_TOOL_PART,
  HistoryExchange,
  KnowledgeSource,
  LABEL_KEYS_DATA_PART,
  SOURCES_DATA_PART
} from '@glow/ai-contract'
import { ChatTransport, getToolName, isToolUIPart } from 'ai'
import { useEffect, useMemo, useRef, useState } from 'react'
import { AiChatUIMessage, createAiChatTransport, messageText } from './aiChatTransport'
import { DeleteAiConversation, FetchAiConversation, FetchAiConversations } from './aiConversations'
import { SendAiFeedback, sendAiFeedback } from './aiFeedback'

export type AiChatStatus = 'idle' | 'streaming' | 'error'

// A tool call surfaced in the answer: running while it executes, done once its result landed.
// detail carries the human-relevant part of the input for tools the drawer knows how to summarize.
export interface AiToolActivity {
  id: string
  tool: string
  detail?: string
  done: boolean
}

export interface AiChatMessage {
  id: string
  role: 'user' | 'assistant'
  text: string
  toolActivity: AiToolActivity[]
  // Present when the backend persisted the exchange; feedback needs it and is hidden without it
  exchangeId?: string
  // Empty when the service answered without retrieval, so the list renders only when non-empty
  sources: KnowledgeSource[]
  // The label keys this answer and the earlier ones were given, sorted and one per line so the value stays
  // equal while it is unchanged; absent for answers stored without them
  labelKeys?: string
  rating?: boolean
  commented?: boolean
}

interface AiFeedbackState {
  rating?: boolean
  commented?: boolean
}

export interface AiChatOptions {
  transport?: ChatTransport<AiChatUIMessage>
  sendFeedback?: SendAiFeedback
  // Consumed by the history view in AiChat; kept here so every seam is injected in one place
  fetchConversations?: FetchAiConversations
  fetchConversation?: FetchAiConversation
  deleteConversation?: DeleteAiConversation
  context?: AiChatContext
}

const exchangeIdOf = (message: AiChatUIMessage | undefined): string | undefined =>
  message?.parts.flatMap((part) => (part.type === EXCHANGE_DATA_PART ? [part.data.exchangeId] : [])).at(0)

// Every tool part renders, so a tool added server-side shows up here without a frontend change;
// known tools contribute an input summary as detail. input-streaming is skipped: the input is not
// complete yet and the phase lasts a blink. The input can also be missing on output-error.
const toolActivityOf = (message: AiChatUIMessage): AiToolActivity[] =>
  message.parts.flatMap((part) => {
    if (!isToolUIPart(part) || part.state === 'input-streaming') {
      return []
    }
    const detail = part.type === GLOSSARY_TOOL_PART && part.input !== undefined ? part.input.term : undefined
    return [
      {
        id: part.toolCallId,
        tool: getToolName(part),
        detail,
        done: part.state === 'output-available' || part.state === 'output-error'
      }
    ]
  })

const sourcesOf = (message: AiChatUIMessage): KnowledgeSource[] =>
  message.parts.flatMap((part) => (part.type === SOURCES_DATA_PART ? part.data.sources : []))

const labelKeysOf = (message: AiChatUIMessage): string[] | undefined =>
  message.parts.find((part) => part.type === LABEL_KEYS_DATA_PART)?.data.keys

const toUIMessages = (conversationId: string, exchange: HistoryExchange): AiChatUIMessage[] => [
  { id: `${exchange.exchangeId}-question`, role: 'user', parts: [{ type: 'text', text: exchange.question }] },
  {
    id: `${exchange.exchangeId}-answer`,
    role: 'assistant',
    parts: [
      { type: 'text', text: exchange.answer },
      // Rebuilt so a reopened conversation still cites its articles
      ...(exchange.sources.length > 0
        ? [{ type: SOURCES_DATA_PART, data: { sources: exchange.sources } } as const]
        : []),
      ...(exchange.labelKeys === undefined
        ? []
        : [{ type: LABEL_KEYS_DATA_PART, data: { keys: exchange.labelKeys } } as const]),
      { type: EXCHANGE_DATA_PART, data: { conversationId, exchangeId: exchange.exchangeId } }
    ]
  }
]

export const useAiChat = (
  { transport, sendFeedback = sendAiFeedback, context }: AiChatOptions = {},
  answerLanguage?: AnswerLanguage
) => {
  const conversationIdRef = useRef<string | undefined>(undefined)
  // useChat owns the messages array, so the feedback given per answer lives here instead
  const [feedbackByExchange, setFeedbackByExchange] = useState<Record<string, AiFeedbackState>>({})

  const chatTransport = useMemo(() => transport ?? createAiChatTransport(context), [transport, context])

  const chat = useChat<AiChatUIMessage>({
    transport: chatTransport,
    // A fast stream can deliver tokens far more often than the browser paints
    throttle: 50,
    onData: (part) => {
      if (part.type === EXCHANGE_DATA_PART) {
        conversationIdRef.current = part.data.conversationId
      }
    }
  })

  const { stop } = chat
  useEffect(
    () => () => {
      stop()
    },
    [stop]
  )

  // A follow-up may name a label an earlier answer's excerpts gave, so each answer allows the keys of every one so far
  const { messages } = chat.messages.reduce<{ messages: AiChatMessage[]; keys: string[] }>(
    (acc, message) => {
      if (message.role !== 'user' && message.role !== 'assistant') {
        return acc
      }
      const exchangeId = exchangeIdOf(message)
      const own = labelKeysOf(message)
      const keys = own === undefined ? acc.keys : [...new Set([...acc.keys, ...own])].sort()
      return {
        keys,
        messages: [
          ...acc.messages,
          {
            id: message.id,
            role: message.role,
            text: messageText(message),
            toolActivity: toolActivityOf(message),
            exchangeId,
            sources: sourcesOf(message),
            ...(own === undefined ? {} : { labelKeys: keys.join('\n') }),
            ...(exchangeId ? feedbackByExchange[exchangeId] : undefined)
          }
        ]
      }
    },
    { messages: [], keys: [] }
  )

  const status: AiChatStatus = chat.status === 'error' ? 'error' : chat.status === 'ready' ? 'idle' : 'streaming'

  const patchFeedback = (exchangeId: string, patch: AiFeedbackState) =>
    setFeedbackByExchange((current) => ({ ...current, [exchangeId]: { ...current[exchangeId], ...patch } }))

  const exchangeIdOfMessage = (messageId: string) =>
    exchangeIdOf(chat.messages.find((message) => message.id === messageId))

  // The rating posts immediately and an optional comment patches the same feedback afterwards
  const rate = async (messageId: string, helpful: boolean) => {
    const exchangeId = exchangeIdOfMessage(messageId)
    if (!exchangeId || feedbackByExchange[exchangeId]?.rating === helpful) {
      return
    }
    const previous = feedbackByExchange[exchangeId]?.rating
    patchFeedback(exchangeId, { rating: helpful })
    const saved = await sendFeedback({ exchangeId, helpful })
    if (!saved) {
      // Revert only if this call's rating is still the one showing, so a failed post
      // that settles late cannot undo a newer rating
      setFeedbackByExchange((current) =>
        current[exchangeId]?.rating === helpful
          ? { ...current, [exchangeId]: { ...current[exchangeId], rating: previous } }
          : current
      )
    }
  }

  const sendComment = async (messageId: string, comment: string) => {
    const exchangeId = exchangeIdOfMessage(messageId)
    const rating = exchangeId ? feedbackByExchange[exchangeId]?.rating : undefined
    const text = comment.trim()
    if (!exchangeId || rating === undefined || !text) {
      return
    }
    const saved = await sendFeedback({ exchangeId, helpful: rating, comment: text })
    if (saved) {
      patchFeedback(exchangeId, { commented: true })
    }
  }

  const reset = () => {
    conversationIdRef.current = undefined
    setFeedbackByExchange({})
    chat.setMessages([])
  }

  // A deleted conversation must not stay on screen or be continued by the next question
  const forgetConversation = (conversationId: string) => {
    if (conversationIdRef.current === conversationId) {
      reset()
    }
  }

  // Rebuilds the chat from a persisted conversation: the exchange data parts bring the thumbs
  // back, helpful seeds the ratings already given and the id threads the next question
  const loadConversation = (history: ConversationHistory) => {
    conversationIdRef.current = history.conversationId
    setFeedbackByExchange(
      Object.fromEntries(
        history.exchanges.flatMap((exchange): [string, AiFeedbackState][] =>
          exchange.helpful === null ? [] : [[exchange.exchangeId, { rating: exchange.helpful }]]
        )
      )
    )
    chat.setMessages(history.exchanges.flatMap((exchange) => toUIMessages(history.conversationId, exchange)))
  }

  return {
    messages,
    status,
    send: (question: string) => {
      chat.sendMessage({ text: question }, { body: { conversationId: conversationIdRef.current, answerLanguage } })
    },
    retry: () => {
      chat.regenerate({ body: { conversationId: conversationIdRef.current, answerLanguage } })
    },
    rate,
    sendComment,
    stop: () => {
      stop()
    },
    reset,
    forgetConversation,
    loadConversation
  }
}
